import * as THREE from 'three';
import type { DistrictKind } from '@somnhold/content';
import type { MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { GlowLayer } from './glow.ts';
import { yawOf, type WallCell, type WallGraph } from './wallgraph.ts';

/**
 * What makes each kind of district read as itself from the far table camera, without ever covering a
 * cell anyone builds on: banners in its colors hanging on the inner face of its curtains and pennants
 * on its towers, and small props crowding the inner foot of its wall (on the wall cells themselves).
 * The paving inside is painted by terrain.ts (`DISTRICT_PAVING`).
 *
 * Cheap on purpose: per district one merged mesh (props, cloth and flames, lit by vertex attributes)
 * and one shared glow-sprite draw for every flame and rune. The cloth sways in the vertex shader; a
 * district rising (`rise`) and its banners unfurling (`unfurl`) are two uniforms of that same mesh.
 */

/** A district's heraldry: banner field, its dark and light charges, and the color of its lights. */
export const DISTRICT_LOOK: Record<DistrictKind, { field: string; dark: string; light: string; glow: string }> = {
  military: { field: '#a3221c', dark: '#151519', light: '#e8e9ec', glow: '#ff8a3a' },
  quarry: { field: '#6d7684', dark: '#2a2e35', light: '#dfe2e8', glow: '#ffb070' },
  sacred: { field: '#e3ebf6', dark: '#5f9ee8', light: '#ffffff', glow: '#a9d2ff' },
  guild: { field: '#1d8a83', dark: '#0f3b3a', light: '#d9f0ec', glow: '#ffb067' },
};

const WOOD = '#4a3424';
const DWOOD = '#33261b';
const STEEL = '#9aa2ad';
const IRON = '#2c2e33';

/** Shared prototypes (positions and normals only; the kit paints and places them). */
const P = (() => {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const cloth = new THREE.BoxGeometry(1, 1, 1, 1, 5, 1);
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
  const cyl6 = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
  const menhir = new THREE.CylinderGeometry(0.32, 0.5, 1, 4).translate(0, 0.5, 0);
  const cone = new THREE.ConeGeometry(0.5, 1, 6);
  const tail = new THREE.ConeGeometry(0.5, 1, 3);
  const pennant = new THREE.ConeGeometry(0.5, 1, 4).rotateZ(-Math.PI / 2).translate(0.5, 0, 0);
  const ball = new THREE.IcosahedronGeometry(0.5, 0);
  const rock = new THREE.DodecahedronGeometry(0.5, 0);
  const bowl = new THREE.SphereGeometry(0.5, 8, 3, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2);
  return { box, cloth, cyl, cyl6, menhir, cone, tail, pennant, ball, rock, bowl };
})();

const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

/** Deterministic hash in [0, 1) (decor only). */
const hash = (n: number): number => {
  let h = Math.imul(n ^ 0x5bd1e995, 0x27d4eb2d);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
};

const VERT_HEAD = /* glsl */ `
attribute float aTop;
attribute float aGlow;
attribute float aPh;
uniform float uRise;
uniform float uUnfurl;
uniform float uTime;
uniform float uSway;
varying float vGlow;
`;

const VERT_BODY = /* glsl */ `
#include <begin_vertex>
vGlow = aGlow;
if (aTop > -0.5) {
  // Cloth hangs from aTop: unfurling lets it down, the wind stirs it more toward its hem.
  float d = max(0.0, aTop - transformed.y);
  transformed.y = aTop - d * uUnfurl;
  float sw = sin(uTime * 2.3 + aPh + transformed.y * 9.0) * d * 0.16 * uSway;
  transformed.x += sw * cos(aPh);
  transformed.z += sw * sin(aPh);
}
transformed.y -= (1.0 - uRise) * 2.4;
`;

interface Glow {
  p: THREE.Vector3;
  color: THREE.Color;
  size: number;
  op: number;
  flick: number;
  seed: number;
}

interface DistrictMesh {
  side: number;
  kind: DistrictKind;
  mesh: THREE.Mesh | null;
  mat: THREE.MeshStandardMaterial;
  u: { uRise: { value: number }; uUnfurl: { value: number }; uTime: { value: number }; uSway: { value: number } };
  glows: Glow[];
  /** What its dressing was last made from (see `inputs`): the same again and it stays as it is. */
  made?: string;
}

/** The uniforms of a cloth-and-props mesh lit by vertex attributes (districts, and the group's ensign: engine/ensign.ts). */
export type DistrictUniforms = DistrictMesh['u'];

/** The material of such a mesh: one shader program for every district and the ensign (its cache key is shared). */
export const districtMaterial = (u: DistrictUniforms): THREE.MeshStandardMaterial => {
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0, flatShading: true });
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = VERT_HEAD + sh.vertexShader.replace('#include <begin_vertex>', VERT_BODY);
    sh.fragmentShader = 'varying float vGlow;\n' + sh.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      '#include <emissivemap_fragment>\n  totalEmissiveRadiance += diffuseColor.rgb * vGlow * 2.2;',
    );
  };
  mat.customProgramCacheKey = () => 'somnhold-district';
  return mat;
};

export class DistrictDressing {
  readonly group = new THREE.Group();
  readonly glow = new GlowLayer(1024, THREE.AdditiveBlending, 0.6);
  private ds: DistrictMesh[] = [];

  constructor(
    map: MapDef,
    private low: boolean,
  ) {
    this.group.name = 'districts';
    this.glow.mesh.renderOrder = 11;
    this.group.add(this.glow.mesh);
    for (const d of map.districts ?? []) {
      const u = { uRise: { value: 1 }, uUnfurl: { value: 1 }, uTime: { value: 0 }, uSway: { value: low ? 0 : 1 } };
      this.ds.push({ side: d.side, kind: d.kind, mesh: null, mat: districtMaterial(u), u, glows: [] });
    }
  }

  get empty(): boolean {
    return this.ds.length === 0;
  }

  /**
   * Everything `dress` reads for one district: its wall runs and towers, where they stand and face, which are gates
   * or big towers, and which still stand. The walls are rebuilt each time a stretch wears in a fight; most of those
   * changes leave every district's dressing as it was, and remaking it (a few thousand pieces merged) cost a phone
   * 100 ms in the middle of a wave.
   */
  private inputs(d: DistrictMesh, g: WallGraph, big: ReadonlySet<string>, standing: (c: WallCell) => boolean): string {
    const mine = (c: WallCell): boolean => c.district?.side === d.side;
    const at = (c: WallCell): string => `${c.key}${c.gate ? 'g' : ''}${standing(c) ? '' : 'x'}`;
    const out: string[] = [];
    for (const l of g.links) if (mine(l.a) || mine(l.b)) out.push(`${at(l.a)}-${at(l.b)}@${l.a.p.toArray()}/${l.b.p.toArray()}>${l.out.toArray()}`);
    for (const c of g.cells.values()) if (mine(c)) out.push(`${at(c)}${big.has(c.key) ? 'B' : ''}@${c.p.toArray()}>${c.out.toArray()}`);
    return out.join(';');
  }

  /** (Re)dresses the district walls as they now stand (called whenever the walls are rebuilt). */
  walls(g: WallGraph, big: ReadonlySet<string>, broken: ReadonlySet<string>, wear: ReadonlyMap<string, number>): void {
    const standing = (c: WallCell): boolean => !broken.has(c.key) && (wear.get(c.key) ?? 0) < 2;
    for (const d of this.ds) {
      const made = this.inputs(d, g, big, standing);
      if (made === d.made) continue;
      d.made = made;
      if (d.mesh) {
        this.group.remove(d.mesh);
        d.mesh.geometry.dispose();
        d.mesh = null;
      }
      d.glows = [];
      const kit = new Kit({ aTop: -1, aGlow: 0, aPh: 0 });
      this.dress(d, kit, g, big, broken, wear);
      d.mesh = kit.build(d.mat, `district:${d.side}`);
      if (d.mesh) {
        d.mesh.frustumCulled = false;
        this.group.add(d.mesh);
      }
    }
  }

  private dress(d: DistrictMesh, kit: Kit, g: WallGraph, big: ReadonlySet<string>, broken: ReadonlySet<string>, wear: ReadonlyMap<string, number>): void {
    const look = DISTRICT_LOOK[d.kind];
    const glowC = new THREE.Color(look.glow);
    const mine = (c: WallCell): boolean => c.district?.side === d.side;
    const standing = (c: WallCell): boolean => !broken.has(c.key) && (wear.get(c.key) ?? 0) < 2;
    const low = this.low;
    let ph = d.side * 1.7;
    const cloth = (top: number): Record<string, number> => ({ aTop: top, aPh: (ph += 2.399) });
    const lit = { aGlow: 1 };
    const glow = (p: THREE.Vector3, color: THREE.Color, size: number, op: number, flick = 0.4): void => {
      d.glows.push({ p, color, size, op, flick, seed: hash(d.glows.length * 31 + d.side) * 1000 });
    };
    /** A frame at p facing the ground inside (+Z inward, +X along the wall). */
    const frame = (p: THREE.Vector3, inward: THREE.Vector2): THREE.Matrix4 => place(p.x, p.y, p.z, yawOf(inward));
    const at = (m: THREE.Matrix4, x: number, y: number, z: number): THREE.Vector3 => V(x, y, z).applyMatrix4(m);

    /* --- heraldry --- */

    /** A banner hanging from y = top on a face at local z, `w` wide and `h` long, with the kind's charge. */
    const banner = (m: THREE.Matrix4, x: number, top: number, z: number, w: number, h: number): void => {
      const c = cloth(at(m, 0, top, 0).y);
      kit.add(P.box, DWOOD, m, place(x, top + 0.012, z, 0, V(w + 0.06, 0.022, 0.03)));
      kit.add(P.cloth, look.field, m, place(x, top - h / 2, z + 0.004, 0, V(w, h, 0.012)), c);
      for (const s of [-1, 1]) kit.add(P.tail, look.field, m, place(x + s * w * 0.25, top - h - 0.035, z + 0.004, 0, V(w * 0.5, 0.07, 0.012), Math.PI), c);
      const cz = z + 0.012;
      switch (d.kind) {
        case 'military':
          // Black chevron and border on red.
          for (const s of [-1, 1]) kit.add(P.box, look.dark, m, place(x + s * w * 0.2, top - h * 0.55, cz, 0, V(w * 0.55, h * 0.09, 0.008), 0, s * 0.7), c);
          kit.add(P.box, look.dark, m, place(x, top - h * 0.12, cz, 0, V(w, h * 0.08, 0.008)), c);
          break;
        case 'quarry':
          // Crossed hammer and pick in pale stone over a dark band.
          kit.add(P.box, look.dark, m, place(x, top - h * 0.78, cz, 0, V(w, h * 0.16, 0.008)), c);
          for (const s of [-1, 1]) kit.add(P.box, look.light, m, place(x, top - h * 0.42, cz, 0, V(w * 0.7, h * 0.06, 0.008), 0, s * 0.8), c);
          break;
        case 'sacred':
          // A pale-blue sun on white, with a blue hem.
          kit.add(P.cyl, look.dark, m, place(x, top - h * 0.42, cz, 0, V(w * 0.5, 0.008, w * 0.5), Math.PI / 2), c);
          kit.add(P.cyl, look.light, m, place(x, top - h * 0.42, cz + 0.006, 0, V(w * 0.26, 0.008, w * 0.26), Math.PI / 2), c);
          kit.add(P.box, look.dark, m, place(x, top - h * 0.9, cz, 0, V(w, h * 0.06, 0.008)), c);
          break;
        case 'guild':
          // A pale key on teal, between two dark bands.
          for (const y of [0.14, 0.86]) kit.add(P.box, look.dark, m, place(x, top - h * y, cz, 0, V(w, h * 0.07, 0.008)), c);
          kit.add(P.box, look.light, m, place(x, top - h * 0.5, cz, 0, V(w * 0.09, h * 0.5, 0.008)), c);
          kit.add(P.cyl, look.light, m, place(x, top - h * 0.27, cz, 0, V(w * 0.3, 0.008, w * 0.3), Math.PI / 2), c);
          kit.add(P.box, look.light, m, place(x + w * 0.1, top - h * 0.68, cz, 0, V(w * 0.2, h * 0.06, 0.008)), c);
          break;
      }
    };
    /** A pennant on a pole standing at local (0, y0, 0). */
    const pennant = (m: THREE.Matrix4, y0: number, tall: number): void => {
      kit.add(P.cyl, DWOOD, m, place(0, y0 + tall / 2, 0, 0, V(0.024, tall, 0.024)));
      kit.add(P.ball, look.dark, m, place(0, y0 + tall + 0.02, 0, 0, 0.045));
      const c = cloth(at(m, 0, y0 + tall, 0).y + 0.05);
      kit.add(P.pennant, look.field, m, place(0.01, y0 + tall - 0.07, 0, 0, V(0.36, 0.12, 0.02)), c);
      kit.add(P.pennant, look.dark, m, place(0.01, y0 + tall - 0.07, 0.004, 0, V(0.14, 0.05, 0.022)), c);
    };

    /* --- props for the inner foot of the wall (local frame: wall face behind, ground inside at +Z) --- */

    const props: Record<DistrictKind, ((m: THREE.Matrix4, k: number) => void)[]> = {
      military: [
        // A weapon rack: spears and a shield leaning on it.
        (m) => {
          for (const s of [-1, 1]) kit.add(P.box, WOOD, m, place(s * 0.11, 0.11, 0, 0, V(0.022, 0.22, 0.022)));
          kit.add(P.box, WOOD, m, place(0, 0.18, 0, 0, V(0.26, 0.02, 0.02)));
          kit.add(P.box, WOOD, m, place(0, 0.05, 0.02, 0, V(0.26, 0.02, 0.05)));
          for (let i = 0; i < 4; i++) {
            const x = -0.08 + i * 0.055;
            kit.add(P.cyl, WOOD, m, place(x, 0.16, -0.01, 0, V(0.012, 0.32, 0.012), -0.22));
            kit.add(P.cone, STEEL, m, place(x, 0.33, -0.05, 0, V(0.022, 0.06, 0.022), -0.22));
          }
          kit.add(P.cyl, look.field, m, place(0.16, 0.08, 0.03, 0, V(0.13, 0.016, 0.13), Math.PI / 2 - 0.25));
          kit.add(P.cyl, look.dark, m, place(0.16, 0.08, 0.04, 0, V(0.045, 0.02, 0.045), Math.PI / 2 - 0.25));
        },
        // A training dummy, hacked about, a red rag tied on.
        (m, k) => {
          const x = (k - 0.5) * 0.12;
          kit.add(P.cyl, WOOD, m, place(x, 0.15, 0.04, 0, V(0.022, 0.3, 0.022)));
          kit.add(P.box, WOOD, m, place(x, 0.22, 0.04, k * 0.5, V(0.22, 0.02, 0.02)));
          kit.add(P.cyl, '#5c4a38', m, place(x, 0.2, 0.04, 0, V(0.085, 0.13, 0.085)));
          kit.add(P.ball, '#5c4a38', m, place(x, 0.31, 0.04, 0, 0.07));
          kit.add(P.box, look.field, m, place(x, 0.23, 0.045, 0, V(0.1, 0.02, 0.1), 0, 0.3));
          kit.add(P.box, '#2a2622', m, place(x + 0.13, 0.03, 0.06, 0.4, V(0.12, 0.05, 0.06)));
        },
        // Shields stacked against the wall and a barrel of arrows.
        (m) => {
          for (let i = 0; i < 3; i++) {
            const x = -0.12 + i * 0.09;
            kit.add(P.cyl, i % 2 ? look.dark : look.field, m, place(x, 0.075, 0.015 + i * 0.012, 0, V(0.13, 0.016, 0.13), Math.PI / 2 - 0.2));
            kit.add(P.cyl, STEEL, m, place(x, 0.075, 0.027 + i * 0.012, 0, V(0.04, 0.02, 0.04), Math.PI / 2 - 0.2));
          }
          kit.add(P.cyl, '#4a3526', m, place(0.17, 0.06, 0.04, 0, V(0.08, 0.12, 0.08)));
          for (let i = 0; i < 5; i++) kit.add(P.cyl, WOOD, m, place(0.15 + (i % 3) * 0.02, 0.15, 0.03 + (i >> 1) * 0.02, 0, V(0.006, 0.14, 0.006), (i - 2) * 0.08));
        },
      ],
      quarry: [
        // Dressed blocks, stacked for the masons.
        (m, k) => {
          const n = 3 + Math.floor(k * 3);
          for (let i = 0; i < n; i++) {
            const row = i < 3 ? 0 : 1;
            const x = row === 0 ? -0.11 + i * 0.11 : -0.055 + (i - 3) * 0.11;
            const grey = new THREE.Color().setHSL(0.6, 0.04, 0.42 + hash(i * 7 + Math.floor(k * 99)) * 0.1);
            kit.add(P.box, grey, m, place(x, 0.04 + row * 0.075, 0.03, (hash(i + 3) - 0.5) * 0.2, V(0.1, 0.07, 0.09)));
          }
        },
        // A hand cart with a load of stone.
        (m, k) => {
          const yaw = (k - 0.5) * 0.6;
          const c = new THREE.Matrix4().multiplyMatrices(m, place(0, 0, 0.04, yaw));
          kit.add(P.box, WOOD, c, place(0, 0.07, 0, 0, V(0.22, 0.05, 0.13)));
          for (const s of [-1, 1]) kit.add(P.box, DWOOD, c, place(0, 0.1, s * 0.065, 0, V(0.22, 0.04, 0.012)));
          for (const s of [-1, 1]) kit.add(P.cyl, DWOOD, c, place(0.02, 0.045, s * 0.08, 0, V(0.09, 0.02, 0.09), Math.PI / 2));
          for (const s of [-1, 1]) kit.add(P.box, WOOD, c, place(-0.17, 0.08, s * 0.05, 0, V(0.14, 0.015, 0.015), 0, -0.25));
          for (let i = 0; i < 3; i++) kit.add(P.rock, '#74777c', c, place(-0.05 + i * 0.05, 0.12, (i - 1) * 0.03, i, 0.06));
        },
        // Rubble and spoil from the cutting.
        (m, k) => {
          for (let i = 0; i < 6; i++) {
            const grey = new THREE.Color().setHSL(0.58, 0.05, 0.42 + hash(i * 13 + Math.floor(k * 77)) * 0.18);
            kit.add(P.rock, grey, m, place(-0.14 + hash(i * 3 + 1) * 0.28, 0.02, 0.02 + hash(i * 5 + 2) * 0.08, i, 0.05 + hash(i) * 0.05));
          }
          kit.add(P.box, IRON, m, place(0.12, 0.012, 0.06, 0.6, V(0.12, 0.01, 0.015)));
        },
      ],
      sacred: [
        // A cluster of votive candles.
        (m, k) => {
          const n = 4 + Math.floor(k * 3);
          for (let i = 0; i < n; i++) {
            const a = i * 2.4 + k;
            const r = 0.02 + (i / n) * 0.1;
            const x = Math.cos(a) * r;
            const z = 0.05 + Math.abs(Math.sin(a)) * r * 0.6;
            const h = 0.04 + hash(i * 9 + Math.floor(k * 50)) * 0.06;
            kit.add(P.cyl6, '#e9ecef', m, place(x, h / 2, z, 0, V(0.022, h, 0.022)));
            kit.add(P.cone, '#ffe2b0', m, place(x, h + 0.014, z, 0, V(0.014, 0.03, 0.014)), lit);
            if (i % 2 === 0) glow(at(m, x, h + 0.02, z), new THREE.Color('#ffd9b0'), 0.16, 0.7, 1);
          }
        },
        // A small standing stone with a rune that glows.
        (m, k) => {
          const x = (k - 0.5) * 0.1;
          kit.add(P.menhir, '#8e98a6', m, place(x, 0, 0.05, k, V(0.09, 0.26, 0.07), (k - 0.5) * 0.1));
          kit.add(P.box, '#bfe0ff', m, place(x, 0.15, 0.05, k, V(0.03, 0.05, 0.075)), lit);
          kit.add(P.rock, '#6f7886', m, place(x + 0.1, 0.02, 0.06, 1, 0.06));
          glow(at(m, x, 0.16, 0.08), new THREE.Color(look.glow), 0.3, 0.5, 0.15);
        },
        // An offering bowl of pale fire between two low stones.
        (m) => {
          kit.add(P.cyl, '#8e98a6', m, place(0, 0.03, 0.05, 0, V(0.06, 0.06, 0.06)));
          kit.add(P.bowl, '#5d6673', m, place(0, 0.1, 0.05, 0, V(0.12, 0.08, 0.12)));
          kit.add(P.ball, '#cfe8ff', m, place(0, 0.1, 0.05, 0, V(0.08, 0.03, 0.08)), lit);
          for (const s of [-1, 1]) kit.add(P.menhir, '#7d8796', m, place(s * 0.12, 0, 0.04, s, V(0.05, 0.12, 0.04)));
          glow(at(m, 0, 0.14, 0.05), new THREE.Color(look.glow), 0.34, 0.55, 0.3);
        },
      ],
      guild: [
        // Crates, one on another.
        (m, k) => {
          const n = 2 + Math.floor(k * 2);
          for (let i = 0; i < n; i++) {
            const s = 0.09 + hash(i * 11 + Math.floor(k * 30)) * 0.03;
            const x = i === 2 ? -0.02 : -0.06 + i * 0.12;
            const y = i === 2 ? 0.1 + s / 2 : s / 2;
            const c = new THREE.Matrix4().multiplyMatrices(m, place(x, y, 0.04, (hash(i) - 0.5) * 0.5));
            kit.add(P.box, '#6a4c33', c, place(0, 0, 0, 0, s));
            for (const z of [-1, 1]) kit.add(P.box, DWOOD, c, place(0, 0, z * (s / 2 + 0.002), 0, V(s * 1.02, 0.014, 0.004), 0, 0.78));
          }
        },
        // Barrels, banded with iron.
        (m, k) => {
          for (let i = 0; i < 2 + Math.floor(k * 2); i++) {
            const x = -0.1 + i * 0.085;
            const z = 0.03 + (i % 2) * 0.03;
            kit.add(P.cyl, '#5a3d28', m, place(x, 0.06, z, 0, V(0.075, 0.12, 0.075)));
            for (const y of [0.025, 0.095]) kit.add(P.cyl, IRON, m, place(x, y, z, 0, V(0.08, 0.008, 0.08)));
          }
        },
        // A workbench with tools, and a lantern hung on a post beside it.
        (m) => {
          kit.add(P.box, WOOD, m, place(-0.03, 0.11, 0.04, 0, V(0.26, 0.022, 0.1)));
          for (const x of [-0.14, 0.08]) for (const z of [0, 0.08]) kit.add(P.box, DWOOD, m, place(x, 0.05, z, 0, V(0.018, 0.1, 0.018)));
          kit.add(P.box, IRON, m, place(-0.09, 0.13, 0.04, 0.3, V(0.06, 0.02, 0.02)));
          kit.add(P.box, STEEL, m, place(0.02, 0.127, 0.05, -0.4, V(0.08, 0.008, 0.016)));
          kit.add(P.cyl, '#5a3d28', m, place(0.05, 0.135, 0.03, 0, V(0.03, 0.03, 0.03)));
          kit.add(P.box, DWOOD, m, place(0.16, 0.17, 0.02, 0, V(0.022, 0.34, 0.022)));
          kit.add(P.box, DWOOD, m, place(0.16, 0.33, 0.06, 0, V(0.018, 0.018, 0.09)));
          kit.add(P.box, IRON, m, place(0.16, 0.29, 0.1, 0, V(0.05, 0.065, 0.05)));
          kit.add(P.box, '#ffcf8a', m, place(0.16, 0.29, 0.1, 0, V(0.035, 0.05, 0.055)), lit);
          glow(at(m, 0.16, 0.29, 0.1), new THREE.Color(look.glow), 0.5, 0.75, 0.5);
        },
      ],
    };

    /* --- on the wall top --- */

    /** Quarry: a derrick on the wall-walk swinging a block up from the ground inside. */
    const derrick = (m: THREE.Matrix4): void => {
      const y0 = 0.7;
      kit.add(P.box, DWOOD, m, place(0, y0 + 0.02, -0.04, 0, V(0.14, 0.04, 0.12)));
      kit.add(P.cyl, WOOD, m, place(0, y0 + 0.24, -0.04, 0, V(0.03, 0.46, 0.03)));
      const jib = place(0, y0 + 0.38, 0.1, 0, V(0.022, 0.022, 0.4), -0.35);
      kit.add(P.box, WOOD, m, jib);
      kit.add(P.cyl, '#8a7a62', m, place(0, y0 + 0.22, 0.27, 0, V(0.006, 0.34, 0.006)));
      kit.add(P.box, '#7f8287', m, place(0, y0 + 0.02, 0.27, 0.3, V(0.08, 0.06, 0.07)));
      kit.add(P.cyl, DWOOD, m, place(0.05, y0 + 0.1, -0.04, 0, V(0.08, 0.03, 0.08), 0, Math.PI / 2));
    };
    /** Military: a brazier on the tower top, burning. */
    const brazier = (m: THREE.Matrix4, y0: number): void => {
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        kit.add(P.box, IRON, m, place(Math.cos(a) * 0.05, y0 + 0.05, Math.sin(a) * 0.05, -a, V(0.012, 0.1, 0.012), 0, 0.3));
      }
      kit.add(P.bowl, IRON, m, place(0, y0 + 0.13, 0, 0, V(0.14, 0.08, 0.14)));
      kit.add(P.cone, '#ff9a4a', m, place(0, y0 + 0.17, 0, 0, V(0.09, 0.14, 0.09)), lit);
      glow(at(m, 0, y0 + 0.2, 0), glowC, 0.7, 0.75, 0.6);
    };

    /* --- place it all --- */

    let n = 0;
    for (const l of g.links) {
      if (!mine(l.a) && !mine(l.b)) continue;
      if (l.a.gate || l.b.gate || !standing(l.a) || !standing(l.b)) continue;
      const k = hash(l.a.q * 131 + l.a.r * 71 + l.b.q * 17 + l.b.r * 5 + d.side);
      const mid = l.a.p.clone().add(l.b.p).multiplyScalar(0.5);
      const inward = l.out.clone().negate();
      const m = frame(mid, inward);
      n++;
      // Banners on every other run, alternating with the props below them.
      if (n % 2 === 0) banner(m, 0, 0.64, 0.2, 0.25, 0.4);
      if (d.kind === 'quarry' && n % 3 === 1) derrick(m);
      if (low && n % 2 === 1) continue;
      const list = props[d.kind];
      const fn = list[Math.floor(k * list.length)] as (typeof list)[number];
      // At the foot of the inner face, off-centre along the run.
      fn(new THREE.Matrix4().multiplyMatrices(m, place((k - 0.5) * 0.3, 0, 0.21)), hash(Math.floor(k * 1e6)));
    }
    for (const c of g.cells.values()) {
      if (!mine(c) || c.gate || !standing(c)) continue;
      const inward = c.out.clone().negate();
      const m = frame(c.p, inward);
      if (big.has(c.key)) {
        // A long banner down the big tower's inner side.
        banner(m, 0, 1.0, 0.4, 0.22, 0.46);
        continue;
      }
      const h = hash(c.q * 977 + c.r * 131 + d.side);
      if (d.kind === 'military' && h < 0.5) brazier(m, 0.86);
      else if (d.kind === 'sacred' && h < 0.5) {
        // A pale flame in a bowl on the tower top.
        kit.add(P.bowl, '#5d6673', m, place(0, 0.92, 0, 0, V(0.14, 0.08, 0.14)));
        kit.add(P.cone, '#d6ecff', m, place(0, 0.97, 0, 0, V(0.08, 0.14, 0.08)), lit);
        glow(at(m, 0, 1.02, 0), glowC, 0.5, 0.6, 0.35);
      } else if (d.kind === 'guild' && h < 0.5) {
        kit.add(P.box, DWOOD, m, place(0, 1.0, 0, 0, V(0.02, 0.2, 0.02)));
        kit.add(P.box, IRON, m, place(0, 1.12, 0, 0, V(0.06, 0.08, 0.06)));
        kit.add(P.box, '#ffcf8a', m, place(0, 1.12, 0, 0, V(0.045, 0.06, 0.065)), lit);
        glow(at(m, 0, 1.12, 0), glowC, 0.6, 0.8, 0.5);
      } else pennant(m, 0.86, 0.62);
      // Something at the tower's inner foot too, now and then.
      if (!low && h > 0.55) {
        const list = props[d.kind];
        const fn = list[Math.floor(hash(c.q * 7 + c.r) * list.length)] as (typeof list)[number];
        fn(new THREE.Matrix4().multiplyMatrices(m, place(0, 0, 0.3)), h);
      }
    }
  }

  /** A district rising out of the ground (0 buried … 1 standing) and its banners unfurling (0 … 1). */
  rise(side: number, rise: number, unfurl: number): void {
    for (const d of this.ds) {
      if (d.side !== side) continue;
      d.u.uRise.value = rise;
      d.u.uUnfurl.value = unfurl;
    }
  }

  /** Every animation frame: the wind in the cloth and the flicker of every flame. */
  frame(t: number): void {
    const tick = Math.floor(t * 12);
    this.glow.begin();
    for (const d of this.ds) {
      d.u.uTime.value = t;
      const k = d.u.uUnfurl.value * d.u.uRise.value;
      if (k <= 0) continue;
      for (const g of d.glows) {
        const f = g.flick > 0 ? 1 - g.flick * 0.4 * hash(tick * 31 + Math.floor(g.seed)) : 1;
        this.glow.add(g.p.x, g.p.y, g.p.z, g.color, g.size * (0.92 + 0.08 * f), g.op * f * k);
      }
    }
    this.glow.end();
  }

  dispose(): void {
    for (const d of this.ds) {
      d.mesh?.geometry.dispose();
      d.mat.dispose();
    }
    this.glow.material.dispose();
  }
}
